"use client";

import {
  Description,
  HighlightsContainer,
  MobileDescriptionContainer,
  MobileProductAttributesContainer,
  MobileTopBlockContainer,
  PearlPage,
  PricingActionsMobileWrapper,
  ProductAttributes,
  ProductComparison,
  ProductContainer,
  ScrollContainer,
  TopBlock
} from "@growth/page.products";
import "@growth/page.products/style.css";
import type {
  ItemType,
  MembershipPricingOption,
  MultipleProductCard,
  PDP,
  PricingOption,
  ProductCardPrices,
  SingularProductCard
} from "@shared/graphql.types";
import { InfoBlock } from "@shared/ui.components";
import { useStrikethrough } from "@shared/ui.hooks";
import { Typography } from "@shared/ui.primitives";
import "@shared/ui.primitives/style.css";
import { dispatch } from "@shared/utils.events";
import { navigate } from "@shared/utils.routing";
import debounce from "lodash.debounce";
import { useMemo, useState } from "react";

import { ATBConfirmation } from "basket_and_checkout/shared/components/ATBConfirmation";
import { LoadingMask } from "components/LoadingMask";
import { useCurrencySymbol, useLocale } from "hooks";
import { useUserV2 } from "hooks/useUserV2";
import { useVariantIds } from "hooks/useVariantIds";
import { addWishlistItemBySKU, removeWishlistItemBySKU } from "lib/api";
import { getTrackingEventId } from "lib/api/utils/trackingEventId";
import {
  ClinicalTrials,
  ExtraProductInfo,
  HowToUse,
  onAddToBag,
  PairsWithExperimentContainer,
  PerfectFor,
  PricingOptions,
  Recommendations,
  ReviewsLayout,
  TraditionalRetail
} from "packages/acquisition/components";
import { CoreProductInfoContainer } from "packages/acquisition/components/CoreProductInfoContainer/CoreProductInfoContainer";
import { Highlights } from "packages/acquisition/components/Highlights/Highlights";
import { isNoDefaultExperiment } from "packages/acquisition/components/PricingOptions/noDefaultExperiment.utils";
import { PricingOptionsExperiment2 } from "packages/acquisition/components/PricingOptions/PricingOptionsExperiment2";
import { Gallery } from "packages/acquisition/Gallery/Gallery";

import { useOOSEmailReminder } from "./hooks/useOOSEmailReminder";
import { useTrackProductViewed } from "./hooks/useTrackProductViewed";
import { useProductComparison } from "./hooks/useProductComparison";
import { PromotionVisibilityPDPExperiment } from "./promotions/PromotionVisibilityPDPExperiment";

/**
 * Determines whether the PairsWithExperimentContainer should be rendered
 * based on the experiment value being a valid number >= 0
 */
export const shouldRenderPairsWithExperimentContainer = (
  pairsWithExperimentValue?: number
): boolean => {
  return Boolean(
    pairsWithExperimentValue !== undefined &&
      isNaN(pairsWithExperimentValue) === false &&
      pairsWithExperimentValue >= 0
  );
};

type PageProps = {
  productDetails: PDP;
  useCloudinary: boolean;
  membershipInBag: boolean;
  isMember: boolean;
  isFreeTrialVariant?: boolean;
  promoBanner?: {
    title: string;
    colour: string;
  };
  productPrices?: ProductCardPrices;
  pairsWithExperimentValue?: number;
  pairsWithProductCards?: (SingularProductCard | MultipleProductCard)[];
  noDefaultPageExperimentVariant?: number;
  freeTrialPATBVariant?: number;
  pdpComparisonTableExperimentValue?: number;
};

export const Page: React.FC<PageProps> = ({
  productDetails,
  useCloudinary,
  membershipInBag,
  isMember,
  promoBanner,
  isFreeTrialVariant = false,
  productPrices,
  pairsWithExperimentValue = -1,
  pairsWithProductCards,
  noDefaultPageExperimentVariant,
  freeTrialPATBVariant,
  pdpComparisonTableExperimentValue
}) => {
  const eventId = useMemo(() => getTrackingEventId(), []);

  const {
    shouldRenderComparison,
    onComparisonTableViewed,
    onComparisonTableScrolled
  } = useProductComparison({
    pdpComparisonTableExperimentValue,
    slug: productDetails.coreProductInfo.slug
  });

  const { gallery, coreProductInfo, productDeepDive } = productDetails;
  const [confirmation, setConfirmation] = useState(false);
  const [confirmationVariant, setConfirmationVariant] = useState(
    coreProductInfo.sku
  );
  const { wishlist, isLoggedIn, hasMembership } = useUserV2();
  const locale = useLocale();
  const currencySymbol = useCurrencySymbol();

  useTrackProductViewed(productDetails, eventId);

  const {
    size,
    madeIn,
    standards,
    prices,
    sku,
    title,
    brandName,
    subtitle,
    review,
    infoBanner,
    ribbons,
    relatedVariants,
    slug,
    membershipOptions,
    pricingOptions,
    id,
    isOutOfStock,
    description,
    badge,
    maxItemQuantity,
    isShadeMatchingEnabled
  } = coreProductInfo;
  const {
    perfectFor,
    productBenefits,
    listOfIngredients,
    candleSafety,
    highlights,
    howToUse,
    trials,
    traditionalRetail
  } = productDeepDive;

  const { promoPrice, price, setActiveUserType } = useStrikethrough(
    productPrices,
    hasMembership || pricingOptions.data.defaultPrice === "2"
  );

  const attributes = [size, madeIn, ...standards];
  const filteredAttributes = attributes.filter(
    (attribute) => typeof attribute === "string"
  );

  const { giftMembershipProductId } = useVariantIds();
  const isGiftMembership = id === giftMembershipProductId;

  const handleAddRemoveToWishlist = debounce((isInWishlist: boolean) => {
    if (isInWishlist) removeWishlistItemBySKU(sku);
    else addWishlistItemBySKU(sku);
  }, 500);
  const oosEmailReminder = useOOSEmailReminder(sku);

  const isInWishlist = wishlist?.some(
    (item: ItemType) => item.variant?.sku === sku
  );
  const handleWishlist = () => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    handleAddRemoveToWishlist(isInWishlist);
  };

  const handleOutOfStockSubmit = ({ email }: { email: string }) => {
    return oosEmailReminder.handleOutOfStockEmail(email, locale);
  };

  const onATB = ({ membershipSKU, locationTypeDetails }) => {
    setConfirmationVariant(sku);
    return onAddToBag({
      variant: { id, sku, maxItemQuantity },
      isGiftMembership,
      locale,
      hasMembership,
      setConfirmation
    })({ membershipSKU, locationTypeDetails });
  };

  const PairsWithATB = (variantSku: string) => {
    setConfirmationVariant(variantSku);
    setConfirmation(true);
  };

  const trackReviewsClick = () => {
    dispatch("page element clicked", {
      target: "PDP - Reviews Link",
      details: "Scroll To Reviews",
      location: "PDP - Core Product Info"
    });
  };
  const onUpdatePrice = (
    updatedPrice: PricingOption | MembershipPricingOption
  ) => {
    setActiveUserType(
      (updatedPrice as MembershipPricingOption).membership ? "member" : "guest"
    );
  };

  const renderPairsWithContainer = shouldRenderPairsWithExperimentContainer(
    pairsWithExperimentValue
  );

  const renderHighlights = () => {
    return highlights &&
      (!perfectFor.items || !perfectFor.image) &&
      !productBenefits ? (
      <HighlightsContainer>
        <Highlights text={highlights ?? ""} />
      </HighlightsContainer>
    ) : null;
  };

  const renderProductAttributes = () => {
    return filteredAttributes?.length ? (
      <MobileProductAttributesContainer>
        <ProductAttributes attributes={filteredAttributes} />
      </MobileProductAttributesContainer>
    ) : null;
  };

  const renderTraditionalRetail = () => {
    return sku.startsWith("BP_") === false ? (
      <TraditionalRetail traditionalRetail={traditionalRetail} />
    ) : null;
  };

  const renderProductComparisonTable = () => {
    if (shouldRenderComparison) {
      return (
        <ProductComparison
          locale={locale}
          slug={slug}
          onElementViewed={onComparisonTableViewed}
          onScroll={onComparisonTableScrolled}
          currencySymbol={currencySymbol}
          title="How it compares"
          footerCTA={{
            copy: "Discover more about our comparisons in the T&Cs",
            href: "/legal/terms-and-conditions"
          }}
        />
      );
    }
    return null;
  };

  return (
    <PearlPage>
      <script
        id="pdp-seo-tag"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(productDetails.seo)
        }}
      />

      <ATBConfirmation
        variant={{ sku: confirmationVariant }}
        confirmation={confirmation}
        setConfirmation={setConfirmation}
        source="PDP"
        isFreeTrialVariant={isFreeTrialVariant}
      />
      <ProductContainer>
        <ScrollContainer>
          <MobileTopBlockContainer>
            <TopBlock
              brandName={brandName ?? undefined}
              title={title}
              subtitle={subtitle}
              rating={review.averageRating}
              reviewCount={review.count}
              ribbons={ribbons ?? undefined}
              trackReviewsClick={trackReviewsClick}
              promoBanner={promoBanner}
              promoPrice={promoPrice}
              price={price}
            />
            {infoBanner && (
              <InfoBlock>
                <Typography color="primary-dark" variant="body-md">
                  {infoBanner}
                </Typography>
              </InfoBlock>
            )}
          </MobileTopBlockContainer>
          <Gallery
            badge={badge ?? undefined}
            gallery={gallery}
            useCloudinary={useCloudinary}
            shouldRenderComparison={shouldRenderComparison}
          />
          <PromotionVisibilityPDPExperiment />
          <PricingActionsMobileWrapper>
            {isNoDefaultExperiment(noDefaultPageExperimentVariant) ? (
              <PricingOptionsExperiment2
                membershipOptions={membershipOptions}
                pricingOptions={pricingOptions}
                variant={{ sku, prices }}
                onAddToBag={onATB}
                handleWishlist={handleWishlist}
                isInWishlist={isInWishlist}
                relatedVariants={relatedVariants}
                selectedVariantSlug={slug}
                context="mobile"
                handleSubmit={handleOutOfStockSubmit}
                isOutOfStock={isOutOfStock}
                submitted={oosEmailReminder.emailSent}
                membershipInBag={membershipInBag}
                isMember={isMember}
                isFreeTrialVariant={isFreeTrialVariant}
                onPriceChange={onUpdatePrice}
                noDefaultPageExperimentVariant={
                  noDefaultPageExperimentVariant as number
                }
                isShadeMatchingEnabled={isShadeMatchingEnabled}
                hasMembership={hasMembership}
              />
            ) : (
              <PricingOptions
                membershipOptions={membershipOptions}
                pricingOptions={pricingOptions}
                variant={{ sku, prices }}
                onAddToBag={onATB}
                handleWishlist={handleWishlist}
                isInWishlist={isInWishlist}
                relatedVariants={relatedVariants}
                selectedVariantSlug={slug}
                context="mobile"
                handleSubmit={handleOutOfStockSubmit}
                isOutOfStock={isOutOfStock}
                submitted={oosEmailReminder.emailSent}
                membershipInBag={membershipInBag}
                isMember={isMember}
                isFreeTrialVariant={isFreeTrialVariant}
                onPriceChange={onUpdatePrice}
                freeTrialPATBVariant={freeTrialPATBVariant}
                isShadeMatchingEnabled={isShadeMatchingEnabled}
                hasMembership={hasMembership}
              />
            )}
          </PricingActionsMobileWrapper>
          <MobileDescriptionContainer>
            <Description text={description ?? ""} />
          </MobileDescriptionContainer>
          <PerfectFor perfectFor={perfectFor} />
          {renderPairsWithContainer && (
            <PairsWithExperimentContainer
              experimentVariant={pairsWithExperimentValue}
              productCards={pairsWithProductCards}
              onAddToBag={PairsWithATB}
            />
          )}
          {renderProductAttributes()}
          {renderHighlights()}
          {renderTraditionalRetail()}
          {renderProductComparisonTable()}
          <ExtraProductInfo
            productBenefits={productBenefits ?? undefined}
            ingredientList={listOfIngredients ?? undefined}
            candleSafety={candleSafety ?? undefined}
          />
          {howToUse && <HowToUse {...howToUse} />}
          {trials && <ClinicalTrials trials={trials} />}
        </ScrollContainer>
        <CoreProductInfoContainer
          coreProductInfo={coreProductInfo}
          filteredAttributes={filteredAttributes}
          trackReviewsClick={trackReviewsClick}
          onATB={onATB}
          handleWishlist={handleWishlist}
          isInWishlist={isInWishlist}
          relatedVariants={relatedVariants}
          selectedVariantSlug={slug}
          handleSubmit={handleOutOfStockSubmit}
          submitted={oosEmailReminder.emailSent}
          membershipInBag={membershipInBag}
          isMember={isMember}
          isFreeTrialVariant={isFreeTrialVariant}
          promoBanner={promoBanner}
          promoPrice={promoPrice}
          price={price}
          onPriceChange={onUpdatePrice}
          noDefaultPageExperimentVariant={noDefaultPageExperimentVariant}
          freeTrialPATBVariant={freeTrialPATBVariant}
          hasMembership={hasMembership}
        />
      </ProductContainer>
      <Recommendations isOutOfStock={isOutOfStock} sku={sku} />
      <ReviewsLayout sku={sku} />
      <LoadingMask />
    </PearlPage>
  );
};
